import { supabase } from '@/lib/supabase';

/**
 * The dashboard's data layer. No React in this file — it fetches, joins and
 * derives, and the components render what comes back. Everything here is a
 * READ; the write actions (P3's four RPCs) live in src/lib/actions.ts on
 * purpose, so this file's promise stays checkable at a glance. The browser
 * still holds no table-level write privilege at all (tests/005 pins it) —
 * writes exist only as definer RPCs.
 *
 * Two things worth knowing before touching this file:
 *
 * 1. RLS scopes every query invisibly. A manager sees their own location's
 *    rows; an admin sees everything (AP-D3/DD4). Nothing here filters by
 *    location — the database already did. If a list looks short, that is the
 *    policy working; if it looks EMPTY, see loadDashboardData()'s manager
 *    check before suspecting the query (CLAUDE.md trap #3).
 *
 * 2. THERE ARE TWO POINTS WINDOWS AND THEY DISAGREE ON PURPOSE (migration
 *    0005). `points` here is CALENDAR YEAR TO DATE — the dashboard chip,
 *    resets Jan 1. `activePoints` is ROLLING ONE YEAR TO THE DAY. Q1 was
 *    DECIDED 2026-08-20: threshold warnings fire on CALENDAR YTD (the chip's
 *    number — POINTS_RULES §6), so the chip and the warning emails always
 *    agree. The rolling number stays displayed as information; it drives
 *    nothing. Still do not "unify" them.
 */

// ---------------------------------------------------------------------------
// Row shapes, hand-written against the migrations (0002–0005, 0008).
// Not done (deliberately): generated types via `supabase gen types typescript` — revisit if the schema grows.
// ---------------------------------------------------------------------------

export type StaffRole = 'manager' | 'admin';

export type AbsenceType =
  | 'PTO'
  | 'Called Off'
  | 'Sick'
  | 'Tardy/Late'
  | 'No-Call-No-Show'
  | 'Other Unplanned'
  | 'Emergency';

export type AbsenceStatus =
  | 'Pending'
  | 'Approved'
  | 'Denied'
  | 'Recorded'
  | 'Needs Documentation'
  | 'Voided';

export type NotCountedReason =
  | 'not_pto'
  | 'weekend'
  | 'company_holiday'
  | 'manager_excluded'
  | 'excused';

export interface ManagerRow {
  email: string;
  full_name: string;
  role: StaffRole;
  location_id: string | null; // null = all locations (typically admins)
  active: boolean;
}

export interface LocationRow {
  id: string;
  name: string;
  active: boolean;
  point_thresholds: number[] | null; // null = inherit company default
  pto_allotment: number | null; //      null = inherit company default
}

export interface CompanySettingsRow {
  point_thresholds: number[];
  pto_allotment: number | null; // null = no limit (today's live state)
  notify_employee: boolean;
}

export interface EmployeeRow {
  id: string;
  full_name: string;
  email: string | null;
  location_id: string;
  pto_allotment: number | null; // null = inherit location → company
  active: boolean;
}

/** One row per absence day, from the absence_facts view (M7 + M8). */
export interface AbsenceFactRow {
  id: string;
  request_id: string;
  employee_id: string;
  absence_date: string; // YYYY-MM-DD — parse with fromDateKey(), never new Date()
  absence_type: AbsenceType;
  status: AbsenceStatus;
  /**
   * RAW values as written on the day row — a Pending day carries its would-be
   * pto_days here while counted_pto_days is still 0. P3's approve-time
   * projection (the v22 UNPAID warning) needs the raw number: it is asking
   * "what happens IF this is approved", which counted_* by design won't say.
   */
  points: number;
  pto_days: number;
  counted_points: number;
  counted_pto_days: number;
  not_counted_reason: NotCountedReason | null; // null = counts fully
  not_counted_note: string | null;
  decided_by_email: string | null; // audit field, verbatim; blank on 49 legacy rows (trap #5)
  submitted_at: string | null; //     null only on the one quarantined 07/28/0202 timestamp
  source_row: number | null; //       original Sheet row; null for rows born after cutover
  is_taken: boolean; // DD1, computed against today_local()
  /** P5 (migration 0017): the parent request's free-text notes — DD6's Notes field. */
  notes: string | null;
  /** P5: why a Voided request was voided (void_needs_reason) — null until voided. */
  void_reason: string | null;
  /** 0032: the denied request this one was resubmitted from (denial email link). */
  resubmitted_from: string | null;
}

/** One row per (employee, year) from employee_year_totals — calendar YTD. */
export interface YearTotalsRow {
  employee_id: string;
  yr: number;
  points: number;
  pto_total: number;
  pto_taken: number;
  pto_upcoming: number;
  record_count: number;
  type_counts: Partial<Record<AbsenceType, number>>;
}

// ---------------------------------------------------------------------------
// The assembled view model
// ---------------------------------------------------------------------------

export interface EmployeeDashboardRow {
  employee: EmployeeRow;
  locationName: string;
  /** CALENDAR-YTD points — the dashboard chip. 0 when no absence this year. */
  points: number;
  /** ROLLING one-year points — what the 10/15/20 thresholds compare against. */
  activePoints: number;
  /** DD1: taken (date ≤ today) vs upcoming (date > today) vs remaining. */
  ptoTaken: number;
  ptoUpcoming: number;
  ptoTotal: number;
  /** employee → location → company (the v22 override chain). null = no limit configured. */
  allotment: number | null;
  /** allotment − (taken + upcoming): a booked future day is committed. null when no allotment. */
  remaining: number | null;
  /** The v22 red-chip state. Always false when no allotment is configured. */
  over: boolean;
  /** v21 per-type pills, voided rows already excluded by the view. */
  typeCounts: Partial<Record<AbsenceType, number>>;
}

export interface TeamSummary {
  employeeCount: number;
  totalPtoTaken: number;
  totalPtoUpcoming: number;
  overAllotmentCount: number;
}

export interface DashboardData {
  /**
   * Who is signed in — or null for a session with NO active managers row.
   * null is a real state the UI must explain ("your access has been removed —
   * contact an admin"), NOT render as an empty dashboard: RLS returns zero
   * rows for a revoked identity, so every list below will be empty and only
   * this field says why.
   */
  manager: ManagerRow | null;
  /** The database's date, from today_local() — never the browser clock. */
  today: string;
  year: number;
  employees: EmployeeDashboardRow[];
  summary: TeamSummary;
  /** Recent absence days for the activity log, newest first. */
  recentFacts: AbsenceFactRow[];
  /**
   * Every Pending day (uncapped) plus every live day on the same dates —
   * the approval queue and its coverage-overlap check. Never capped:
   * recentFacts' limit is for the activity log only (audit B1).
   */
  queueFacts: AbsenceFactRow[];
  /**
   * 0032: for each denied request a Pending one was resubmitted from, its
   * dates (sorted YYYY-MM-DD) — the "Resubmitted after…" label in the queue.
   */
  resubmittedFromDates: Record<string, string[]>;
  locations: LocationRow[];
  settings: CompanySettingsRow | null;
}

/** Activity log only; the approval queue has its own uncapped read (queueFacts). */
const ACTIVITY_LOG_LIMIT = 200;

// ---------------------------------------------------------------------------
// Fetch + assemble
// ---------------------------------------------------------------------------

/**
 * RLS denial is zero rows, not an error — so any error here is a REAL failure
 * (network, misconfiguration, a dropped grant) and deserves to throw loudly
 * rather than render as a suspiciously quiet dashboard.
 */
function bail(what: string, error: { message: string }): never {
  throw new Error(`${what}: ${error.message}`);
}

export async function loadDashboardData(): Promise<DashboardData> {
  // One round of parallel reads. At ten employees the payloads are tiny; the
  // Apps Script dashboard's 5-second loads came from re-reading the whole
  // sheet per widget (BUILD_PLAN §4b), and this is the structural fix.
  const [managerRes, todayRes, employeesRes, locationsRes, settingsRes, factsRes] =
    await Promise.all([
      supabase.rpc('current_manager'),
      supabase.rpc('today_local'),
      supabase.from('employees').select('*').eq('active', true).order('full_name'),
      supabase.from('locations').select('*').order('name'),
      supabase.from('company_settings').select('*'),
      supabase
        .from('absence_facts')
        .select('*')
        .order('absence_date', { ascending: false })
        .limit(ACTIVITY_LOG_LIMIT),
    ]);

  if (managerRes.error) bail('current_manager()', managerRes.error);
  if (todayRes.error) bail('today_local()', todayRes.error);
  if (employeesRes.error) bail('employees', employeesRes.error);
  if (locationsRes.error) bail('locations', locationsRes.error);
  if (settingsRes.error) bail('company_settings', settingsRes.error);
  if (factsRes.error) bail('absence_facts', factsRes.error);

  // The approval queue is NOT the activity log above: that read is capped at
  // the 200 most recent days by date, so future-dated PTO could push an old
  // Pending request off the only screen that lists it (audit B1). Read every
  // Pending day, then every live day across the same date span for the
  // coverage-overlap check.
  const pendingRes = await supabase
    .from('absence_facts')
    .select('*')
    .eq('status', 'Pending')
    .order('absence_date', { ascending: true });
  if (pendingRes.error) bail('absence_facts (pending)', pendingRes.error);
  const pending = (pendingRes.data ?? []) as AbsenceFactRow[];
  let queueFacts = pending;
  const first = pending[0];
  const last = pending[pending.length - 1];
  if (first && last) {
    const from = first.absence_date;
    const to = last.absence_date;
    const windowRes = await supabase
      .from('absence_facts')
      .select('*')
      .gte('absence_date', from)
      .lte('absence_date', to)
      .not('status', 'in', '("Denied","Voided")');
    if (windowRes.error) bail('absence_facts (queue window)', windowRes.error);
    const byId = new Map<string, AbsenceFactRow>();
    for (const f of [...pending, ...((windowRes.data ?? []) as AbsenceFactRow[])]) {
      byId.set(f.id, f);
    }
    queueFacts = [...byId.values()];
  }

  // 0032: the denied requests that Pending ones were resubmitted from. Denied
  // rows are outside the queue window above by design, so read them by id.
  const originIds = [
    ...new Set(pending.map((f) => f.resubmitted_from).filter((id): id is string => id !== null)),
  ];
  const resubmittedFromDates: Record<string, string[]> = {};
  if (originIds.length > 0) {
    const originRes = await supabase
      .from('absence_facts')
      .select('request_id, absence_date')
      .in('request_id', originIds)
      .order('absence_date', { ascending: true });
    if (originRes.error) bail('absence_facts (resubmitted from)', originRes.error);
    for (const f of (originRes.data ?? []) as Pick<AbsenceFactRow, 'request_id' | 'absence_date'>[]) {
      (resubmittedFromDates[f.request_id] ??= []).push(f.absence_date);
    }
  }

  // ⚠️ current_manager() returns a COMPOSITE, and a scalar composite function
  // expands to one row of all-NULL fields when it returns NULL — so a revoked
  // identity gets `{email: null, ...}`, not `null`. Check the field, never
  // the object. (The same quirk almost made §4.2's in_scope() a leak —
  // migration 0008's header tells that story.)
  const rawManager = managerRes.data as ManagerRow | null;
  const manager = rawManager && rawManager.email ? rawManager : null;

  const today = todayRes.data as string; // YYYY-MM-DD from the database's clock
  const year = Number(today.slice(0, 4));

  const employees = (employeesRes.data ?? []) as EmployeeRow[];
  const locations = (locationsRes.data ?? []) as LocationRow[];
  // Singleton, but RLS-gated: a non-manager legitimately reads zero rows.
  const settings = ((settingsRes.data ?? [])[0] ?? null) as CompanySettingsRow | null;
  const recentFacts = (factsRes.data ?? []) as AbsenceFactRow[];

  // This year's totals. ⚠️ An employee with no absences has NO ROW in
  // employee_year_totals at all (migration 0005's view comment) — the join
  // below must default to zeros, never drop the employee.
  const totalsRes = await supabase
    .from('employee_year_totals')
    .select('*')
    .eq('yr', year);
  if (totalsRes.error) bail('employee_year_totals', totalsRes.error);
  const totalsByEmployee = new Map(
    ((totalsRes.data ?? []) as YearTotalsRow[]).map((t) => [t.employee_id, t]),
  );

  // The OTHER window plus the allotment, per employee. Ten employees, twenty
  // tiny calls — fine at this scale; batch behind one RPC if the roster ever
  // grows tenfold.
  const perEmployeeEntries = await Promise.all(
    employees.map(async (e) => {
      const ap = await supabase.rpc('active_points', { p_employee: e.id });
      if (ap.error) bail(`active_points(${e.full_name})`, ap.error);

      const al = await supabase.rpc('pto_allotment_for', { p_employee: e.id });
      if (al.error) bail(`pto_allotment_for(${e.full_name})`, al.error);
      // NULL is meaningful (no limit configured) — do not Number(null)=0 it.
      const allotment: number | null =
        al.data === null || al.data === undefined ? null : Number(al.data);

      return [e.id, { activePoints: Number(ap.data ?? 0), allotment }] as const;
    }),
  );
  const perEmployee = new Map(perEmployeeEntries);

  const locationsById = new Map(locations.map((l) => [l.id, l]));

  const rows: EmployeeDashboardRow[] = employees.map((e) => {
    const totals = totalsByEmployee.get(e.id);
    const location = locationsById.get(e.location_id);

    // The v22 override chain (employee → location → company), resolved by the
    // DATABASE — §4.5's pto_allotment_for(), which landed in migration 0009.
    // The client-side copy that used to live here is deleted, as its own
    // comment promised: two implementations of a resolution rule is how
    // col H / col I happened.
    const allotment = perEmployee.get(e.id)?.allotment ?? null;

    const ptoTaken = totals?.pto_taken ?? 0;
    const ptoUpcoming = totals?.pto_upcoming ?? 0;
    const remaining = allotment === null ? null : allotment - (ptoTaken + ptoUpcoming);

    return {
      employee: e,
      locationName: location?.name ?? '—',
      points: totals?.points ?? 0,
      activePoints: perEmployee.get(e.id)?.activePoints ?? 0,
      ptoTaken,
      ptoUpcoming,
      ptoTotal: totals?.pto_total ?? 0,
      allotment,
      remaining,
      over: remaining !== null && remaining < 0,
      typeCounts: totals?.type_counts ?? {},
    };
  });

  const summary: TeamSummary = {
    employeeCount: rows.length,
    totalPtoTaken: rows.reduce((s, r) => s + r.ptoTaken, 0),
    totalPtoUpcoming: rows.reduce((s, r) => s + r.ptoUpcoming, 0),
    overAllotmentCount: rows.filter((r) => r.over).length,
  };

  return {
    manager,
    today,
    year,
    employees: rows,
    summary,
    recentFacts,
    queueFacts,
    resubmittedFromDates,
    locations,
    settings,
  };
}

// ---------------------------------------------------------------------------
// Team calendar (P2 session 3)
// ---------------------------------------------------------------------------

/**
 * One month of absence days for the Team Time-Off Calendar.
 *
 * Denied AND Voided are excluded — that exact pair, deliberately. The live
 * system's getTimeOffCalendarV2 buckets "non-Denied" rows, and BUILD_PLAN D3
 * flags the consequence: once Voided exists, a voided absence would still show
 * someone as off. This is the reader that warning was about (DD2: a voided
 * record "drops out of all points/PTO/dashboard/calendar math"). Pending rows
 * ARE included, as the live system includes them — the UI renders them as
 * tentative rather than hiding them.
 *
 * Location scoping is RLS, not a filter here: a manager's calendar shows their
 * own location because the database says so, not because this query remembered.
 */
export async function loadCalendarMonth(year: number, month: number): Promise<AbsenceFactRow[]> {
  const mm = String(month).padStart(2, '0');
  // Day arithmetic via local Date parts only — never string parsing, which
  // treats YYYY-MM-DD as UTC and lands a day early in Eastern (trap #1).
  const daysInMonth = new Date(year, month, 0).getDate();
  const first = `${year}-${mm}-01`;
  const last = `${year}-${mm}-${String(daysInMonth).padStart(2, '0')}`;

  const res = await supabase
    .from('absence_facts')
    .select('*')
    .gte('absence_date', first)
    .lte('absence_date', last)
    .not('status', 'in', '(Denied,Voided)')
    .order('absence_date');
  if (res.error) bail('absence_facts (calendar)', res.error);
  return (res.data ?? []) as AbsenceFactRow[];
}

/**
 * The month's closure days, for calendar shading — ONE call to the database's
 * closure_days() (migration 0013). The closure rules stay in exactly one
 * place: is_closure_day() carries four deliberate quirks (2027/2028 have
 * eight dates, observed shifts, raw Eves) and a 2,557-day diff proving them —
 * a TypeScript port is the two-implementations failure this project ends.
 */
export async function loadClosureDays(year: number, month: number): Promise<Set<string>> {
  const mm = String(month).padStart(2, '0');
  const daysInMonth = new Date(year, month, 0).getDate();
  const res = await supabase.rpc('closure_days', {
    p_from: `${year}-${mm}-01`,
    p_to: `${year}-${mm}-${String(daysInMonth).padStart(2, '0')}`,
  });
  if (res.error) bail('closure_days', res.error);
  return new Set((res.data ?? []) as string[]);
}

/**
 * Same RPC as `loadClosureDays`, but for an arbitrary [from, to] range rather
 * than a whole month — `closure_days()` already takes explicit bounds, so no
 * month-chunking is needed. Pre-P8 punch list: Log an Absence's on-the-spot
 * PTO-allotment warning needs this to estimate a NEW entry's workday count
 * before it exists as stored `absence_days` rows (unlike the Dashboard's
 * Pending-request warning, which reads `pto_days` already computed by the
 * backend at submission time).
 */
export async function loadClosureDaysBetween(from: string, to: string): Promise<Set<string>> {
  const res = await supabase.rpc('closure_days', { p_from: from, p_to: to });
  if (res.error) bail('closure_days', res.error);
  return new Set((res.data ?? []) as string[]);
}

/**
 * v22's over-allotment PROJECTION — shared between the Dashboard's
 * approve-time warning (an already-pending request) and Log an Absence's
 * on-the-spot equivalent (a not-yet-submitted entry), so the rule and its
 * wording live in exactly one place. Warns; never blocks (same as v22).
 * `requestDays` is the RAW pto_days this decision would add — pass 0 for
 * anything other than a PTO entry with countable days.
 */
export function unpaidWarningFor(
  employeeName: string,
  ptoTotal: number,
  allotment: number | null,
  requestDays: number,
): string | null {
  if (allotment === null || requestDays <= 0) return null;
  const projected = ptoTotal + requestDays;
  if (projected <= allotment) return null;
  const over = projected - allotment;
  return (
    `This puts ${employeeName} at ${projected} of ${allotment} PTO days — ` +
    `${over} day${over === 1 ? '' : 's'} would be UNPAID.`
  );
}

// ---------------------------------------------------------------------------
// CSV export (P2 session 3) — required day one, MIGRATION_PLAN §5
// ---------------------------------------------------------------------------

/**
 * Download the full visible absence history as a CSV.
 *
 * Why this exists at all: §10 item 11 — losing the spreadsheet is survivable
 * only if the data stays exportable into something a human can open and fix.
 * This is that escape hatch, shipped in P2 as promised, before anything
 * depends on it.
 *
 * Two properties worth knowing:
 *  - It is scoped by RLS like every other read: a manager exports exactly what
 *    they are allowed to see, nothing more. There is no "export everything"
 *    override to leak through.
 *  - ⚠️ PostgREST caps responses at max_rows = 1000 (config.toml). The history
 *    is 67 rows and grows by a handful a week, so this is years away — but if
 *    this export ever approaches a thousand rows, page it rather than raising
 *    the cap.
 */
export async function downloadAbsenceHistoryCsv(today: string): Promise<void> {
  const [factsRes, employeesRes] = await Promise.all([
    supabase.from('absence_facts').select('*').order('absence_date'),
    supabase.from('employees').select('id, full_name'),
  ]);
  if (factsRes.error) bail('absence_facts (export)', factsRes.error);
  if (employeesRes.error) bail('employees (export)', employeesRes.error);

  const nameById = new Map(
    ((employeesRes.data ?? []) as Array<{ id: string; full_name: string }>).map((e) => [
      e.id,
      e.full_name,
    ]),
  );

  const header = [
    'date',
    'employee',
    'type',
    'status',
    'counted_points',
    'counted_pto_days',
    'not_counted_reason',
    'not_counted_note',
    'decided_by',
    'submitted_at',
    'source_row',
  ];

  const lines = ((factsRes.data ?? []) as AbsenceFactRow[]).map((f) =>
    [
      f.absence_date,
      nameById.get(f.employee_id) ?? f.employee_id,
      f.absence_type,
      f.status,
      String(f.counted_points),
      String(f.counted_pto_days),
      f.not_counted_reason ?? '',
      f.not_counted_note ?? '',
      f.decided_by_email ?? '',
      f.submitted_at ?? '',
      f.source_row === null || f.source_row === undefined ? '' : String(f.source_row),
    ]
      .map(csvField)
      .join(','),
  );

  const csv = [header.join(','), ...lines].join('\r\n') + '\r\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `allied-pto-absence-history-${today}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** RFC 4180 quoting: wrap when needed, double any inner quotes. */
function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * The activity log's "· not counted" label, with the REASON — the whole point
 * of M8's enum over a bare zero (§5: "the reason is shown, not just the
 * absence of a number").
 */
export function notCountedLabel(reason: NotCountedReason, note: string | null): string {
  const why: Record<NotCountedReason, string> = {
    not_pto: 'not PTO',
    weekend: 'weekend',
    company_holiday: 'company holiday',
    manager_excluded: 'excluded by manager',
    excused: note ? `excused — ${note}` : 'excused',
  };
  return `not counted · ${why[reason]}`;
}
